import { ICE_COLORS, ICE_LOOK, TONE_COLORS } from '@halvard/board3d';
import type { ice } from '@halvard/rules';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { WarriorGlyph } from '../components/WarriorGlyph';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLang, useT } from '../i18n';
import './Page.css';

const FORCE: readonly { type: ice.WarriorType; force: number; moves: number }[] = [
  { type: 'skier', force: 1, moves: 4 },
  { type: 'spear', force: 2, moves: 3 },
  { type: 'huscarl', force: 3, moves: 2 },
];

/** The words of the Learn page that sit around the shared parts. */
interface LearnText {
  thickness: readonly string[];
  hole: string;
  warriorDetail: Readonly<Record<ice.WarriorType, string>>;
  warriorStats: (force: number, moves: number) => string;
  tryIt: string;
}

/**
 * How to play. The rules are prose, so each language has its own page; the swatches, warrior
 * cards and badges are shared.
 */
export function Learn() {
  const t = useT();
  const lang = useLang();
  useDocumentTitle(t.titles.learn);
  return lang === 'ko' ? <LearnKo /> : lang === 'fr' ? <LearnFr /> : <LearnEn />;
}

function LearnEn() {
  const text: LearnText = {
    thickness: ['Thin ice', 'Ice', 'Thick ice', 'Pressure ridge'],
    hole: 'Hole',
    warriorDetail: {
      skier: 'Fast enough to reach a runestone first, but the easiest warrior to shove.',
      spear: 'The ordinary warrior, between the two.',
      huscarl: 'Slow on the ice, but hard to move and hard to stop.',
    },
    warriorStats: (force, moves) => `Force ${force}, ${moves} move points.`,
    tryIt: 'Play the bot',
  };
  return (
    <article className="page-page">
      <h1>How to play</h1>
      <p>
        Two warbands fight on a frozen lake as the thaw begins. <strong>Brand</strong> (orange)
        comes from ranks 1–3 and attacks. <strong>Rime</strong> (teal) holds ranks 7–9 and defends.
        Every game is played on a new lake of 9×9 squares, and each square has an ice thickness from
        0 to 3, or is a hole of open water.
      </p>
      <Swatches text={text} label="Ice thickness" />

      <h2>Winning</h2>
      <ul>
        <li>
          Three <strong>runestones</strong> lie in the middle of the lake: the west, centre and east
          floes of ranks 4–6. Each runestone is a whole <strong>3×3 floe</strong>, not just its
          centre square. A side <strong>holds</strong> a runestone when it has more warriors on that
          floe.
        </li>
        <li>
          After each of its turns, Brand draws <strong>1 Glory for each runestone it holds</strong>.
          <strong> Brand wins with 20 Glory</strong> by the end of round 16.
        </li>
        <li>
          <strong>Rime wins</strong> if Brand doesn’t get there in time.
        </li>
        <li>
          Shoving <strong>every enemy warrior</strong> under the ice wins at once, for either side.
        </li>
      </ul>

      <h2>Setting up</h2>
      <p>
        Once the lake is revealed, the players take turns placing one warrior at a time anywhere in
        their own territory, Brand first. Read the lake before you place: there are no openings to
        learn, only ice. Then Brand moves first.
      </p>

      <h2>Your turn</h2>
      <p>
        Move <strong>one</strong> warrior. It spends its move points, one step at a time, to any of
        the 8 squares around it. A step costs <strong>1 + the difference in thickness</strong>, up
        or down: 1 on level ice, 2 for one level, and so on. Warriors can’t pass through each other
        or enter holes. If none of your warriors can move, you pass.
      </p>

      <h2>Shoving</h2>
      <p>
        There is no capture. The last step of a move may go into an enemy’s square: that is a
        <strong> shove</strong>.
      </p>
      <ul>
        <li>
          <strong>Your strength</strong> is your warrior’s force, +1 for each of your other warriors
          touching the enemy.
        </li>
        <li>
          <strong>Their shield wall</strong> is the enemy’s force, +1 for each of its allies
          touching it.
        </li>
        <li>
          The shove works if your strength is <strong>at least</strong> their shield wall. If it
          would fail, you can’t play it.
        </li>
        <li>
          The enemy slides one square straight away from you, and so does{' '}
          <strong>everyone lined up behind it</strong>, friend or foe. Your warrior takes the square
          it left.
        </li>
        <li>
          A sliding warrior is lost if it ends off the lake or in a hole, or 2 or more levels from
          where it stood: <strong>2 down, it falls</strong> from the ledge and breaks through;{' '}
          <strong>2 up, it is crushed</strong> against the wall of ice. A fall or a crush stops the
          push: nobody beyond it moves.
        </li>
      </ul>

      <h2>The thaw</h2>
      <p>
        At the end of rounds 3, 6, 9 and so on, the ice groans and 2 squares crack. Three rounds
        later each cracked square thaws: it loses a level, and thin ice opens into a hole, taking
        any warrior on it. You see every crack coming, so step off it, or shove an enemy onto it.
      </p>

      <h2>The warriors</h2>
      <p>Each side has 2 of each. Size shows force.</p>
      <Warriors text={text} />

      <h2>Reading the board</h2>
      <p>
        Select a warrior and dark dots show where it can walk. Every enemy it can reach gets a badge
        with your strength against its shield wall: <Badge tone="works">3:2</Badge> works,{' '}
        <Badge tone="sinks-ally">3:2 !</Badge> also sinks one of yours,{' '}
        <Badge tone="fails">1:3</Badge> fails.
      </p>
      <p>
        Click an enemy to see each way to shove it: a line from your warrior, a diamond on the
        square the shove starts from, and an arrow for the direction it pushes. Faint warriors show
        where the line would land, marked when one would go under. Click a diamond to shove from
        there.
      </p>
      <p>
        A cracked square shows fracture lines and shivers now and then, harder as its thaw nears;
        the lines are red when the thaw will open a hole. Every square of a runestone’s floe has a
        thin line just inside its edge, and the stone’s ring on the centre square; both glow in the
        colour of the side that holds it, grey when nobody does.
      </p>
      <TryIt text={text} />
    </article>
  );
}

function LearnFr() {
  const text: LearnText = {
    thickness: ['Glace fine', 'Glace', 'Glace épaisse', 'Crête de pression'],
    hole: 'Trou',
    warriorDetail: {
      skier:
        'Assez rapide pour atteindre une pierre runique le premier, mais le plus facile à pousser.',
      spear: 'Le guerrier ordinaire, entre les deux.',
      huscarl: 'Lent sur la glace, mais dur à déplacer et dur à arrêter.',
    },
    warriorStats: (force, moves) => `Force ${force}, ${moves} points de mouvement.`,
    tryIt: 'Jouer contre le bot',
  };
  return (
    <article className="page-page" lang="fr">
      <h1>Comment jouer</h1>
      <p>
        Deux bandes de guerriers s’affrontent sur un lac gelé au début du dégel.{' '}
        <strong>Brand</strong> (orange) vient des rangées 1 à 3 et attaque. <strong>Rime</strong>{' '}
        (bleu-vert) tient les rangées 7 à 9 et défend. Chaque partie se joue sur un nouveau lac de
        9×9 cases, et chaque case a une épaisseur de glace de 0 à 3, ou est un trou d’eau libre.
      </p>
      <Swatches text={text} label="Épaisseur de la glace" />

      <h2>Gagner</h2>
      <ul>
        <li>
          Trois <strong>pierres runiques</strong> se trouvent au milieu du lac{' '}: les plaques de
          glace ouest, centrale et est des rangées 4 à 6. Chaque pierre runique est une{' '}
          <strong>plaque entière de 3×3 cases</strong>, pas seulement sa case centrale. Un camp{' '}
          <strong>tient</strong> une pierre runique quand il a plus de guerriers sur cette plaque.
        </li>
        <li>
          Après chacun de ses tours, Brand gagne{' '}
          <strong>1 point de Gloire par pierre runique tenue</strong>.{' '}
          <strong>Brand gagne avec 20 de Gloire</strong> avant la fin de la manche 16.
        </li>
        <li>
          <strong>Rime gagne</strong> si Brand n’y arrive pas à temps.
        </li>
        <li>
          Pousser <strong>tous les guerriers ennemis</strong> sous la glace fait gagner aussitôt,
          pour l’un ou l’autre camp.
        </li>
      </ul>

      <h2>La mise en place</h2>
      <p>
        Une fois le lac révélé, les joueurs placent chacun leur tour un guerrier où ils veulent dans
        leur territoire, Brand en premier. Lisez le lac avant de placer{' '}: il n’y a pas
        d’ouverture à apprendre, seulement de la glace. Puis Brand joue en premier.
      </p>

      <h2>Votre tour</h2>
      <p>
        Déplacez <strong>un</strong> guerrier. Il dépense ses points de mouvement, pas à pas, vers
        l’une des 8 cases autour de lui. Un pas coûte <strong>1 + la différence d’épaisseur</strong>
        , en montée comme en descente{' '}: 1 sur glace plane, 2 pour un niveau, et ainsi de suite.
        Les guerriers ne se traversent pas et n’entrent pas dans les trous. Si aucun de vos
        guerriers ne peut bouger, vous passez.
      </p>

      <h2>Pousser</h2>
      <p>
        Il n’y a pas de prise. Le dernier pas d’un déplacement peut entrer sur la case d’un ennemi
        {' '}: c’est une <strong>poussée</strong>.
      </p>
      <ul>
        <li>
          <strong>Votre force</strong> est celle de votre guerrier, +1 pour chacun de vos autres
          guerriers qui touchent l’ennemi.
        </li>
        <li>
          <strong>Son mur de boucliers</strong> est la force de l’ennemi, +1 pour chacun de ses
          alliés qui le touchent.
        </li>
        <li>
          La poussée réussit si votre force est <strong>au moins</strong> égale à son mur de
          boucliers. Si elle échouerait, vous ne pouvez pas la jouer.
        </li>
        <li>
          L’ennemi glisse d’une case droit devant vous, et{' '}
          <strong>tous ceux alignés derrière lui</strong> aussi, amis ou ennemis. Votre guerrier
          prend la case qu’il a quittée.
        </li>
        <li>
          Un guerrier qui glisse est perdu s’il finit hors du lac ou dans un trou, ou à 2 niveaux ou
          plus de sa case{' '}: <strong>2 plus bas, il tombe</strong> de la corniche et passe à
          travers la glace{' '}; <strong>2 plus haut, il est écrasé</strong> contre le mur de glace.
          Une chute ou un écrasement arrête la poussée{' '}: personne au-delà ne bouge.
        </li>
      </ul>

      <h2>Le dégel</h2>
      <p>
        À la fin des manches 3, 6, 9 et ainsi de suite, la glace gronde et 2 cases se fissurent.
        Trois manches plus tard, chaque case fissurée dégèle{' '}: elle perd un niveau, et la glace
        fine devient un trou qui emporte le guerrier posé dessus. Chaque fissure se voit à l’avance
        {' '}: quittez-la, ou poussez-y un ennemi.
      </p>

      <h2>Les guerriers</h2>
      <p>Chaque camp en a 2 de chaque sorte. La taille montre la force.</p>
      <Warriors text={text} />

      <h2>Lire le plateau</h2>
      <p>
        Choisissez un guerrier{' '}: des points sombres montrent où il peut marcher. Chaque ennemi
        qu’il peut atteindre porte un badge avec votre force contre son mur de boucliers{' '}:{' '}
        <Badge tone="works">3:2</Badge> réussit, <Badge tone="sinks-ally">3:2 !</Badge> fait aussi
        couler un des vôtres, <Badge tone="fails">1:3</Badge> échoue.
      </p>
      <p>
        Cliquez un ennemi pour voir chaque façon de le pousser{' '}: une ligne depuis votre
        guerrier, un losange sur la case d’où part la poussée, et une flèche dans le sens où elle
        pousse. Des guerriers pâles montrent où la file arriverait, marqués quand l’un coulerait.
        Cliquez un losange pour pousser depuis là.
      </p>
      <p>
        Une case fissurée montre des lignes de fracture et tremble de temps en temps, plus fort à
        l’approche du dégel{' '}; les lignes sont rouges quand le dégel ouvrira un trou. Chaque case
        de la plaque d’une pierre runique a un trait fin juste à l’intérieur de son bord, et la case
        centrale porte l’anneau de la pierre{' '}; les deux brillent de la couleur du camp qui la
        tient, en gris quand personne ne la tient.
      </p>
      <TryIt text={text} />
    </article>
  );
}

function LearnKo() {
  const text: LearnText = {
    thickness: ['얇은 얼음', '얼음', '두꺼운 얼음', '압력 능선'],
    hole: '구멍',
    warriorDetail: {
      skier: '가장 빨라 룬스톤에 먼저 닿지만, 가장 쉽게 밀려납니다.',
      spear: '둘 사이의 균형 잡힌, 가장 평범한 전사입니다.',
      huscarl: '얼음 위에선 느리지만, 밀어내기도 막아서기도 어려운 정예 전사입니다.',
    },
    warriorStats: (force, moves) => `힘 ${force} · 이동력 ${moves}`,
    tryIt: '봇과 대전하기',
  };
  return (
    <article className="page-page" lang="ko">
      <h1>플레이 방법</h1>
      <p>
        해빙이 시작된 얼어붙은 호수 위에서 두 바이킹 전사단이 맞붙습니다. 주황색{' '}
        <strong>브란드</strong>는 1–3줄에서 출발해 공격하고, 청록색 <strong>라임</strong>은 7–9줄을
        지키며 방어합니다. 게임마다 9×9칸의 새로운 호수가 만들어지고, 각 칸은 0부터 3까지의 얼음
        두께를 가지거나 물이 드러난 구멍입니다.
      </p>
      <Swatches text={text} label="얼음 두께" />

      <h2>승리 조건</h2>
      <ul>
        <li>
          호수 한가운데(4–6줄)에 서쪽·중앙·동쪽, 세 개의 <strong>룬스톤</strong>이 있습니다. 룬스톤
          하나는 가운데 칸만이 아니라 <strong>3×3칸 부빙 전체</strong>입니다. 한 부빙 위에 상대보다
          전사가 더 많으면 그 룬스톤을 <strong>장악</strong>합니다.
        </li>
        <li>
          브란드는 자기 차례가 끝날 때마다 <strong>장악한 룬스톤 하나당 영광 1점</strong>을
          얻습니다. 16라운드 안에 <strong>영광 20점</strong>을 모으면 브란드의 승리입니다.
        </li>
        <li>
          그때까지 20점을 모으지 못하면 <strong>라임의 승리</strong>입니다.
        </li>
        <li>
          <strong>상대 전사를 모두</strong> 얼음 밑으로 빠뜨리면 어느 쪽이든 즉시 승리합니다.
        </li>
      </ul>

      <h2>배치</h2>
      <p>
        호수가 공개되면 브란드부터 한 명씩 번갈아, 자기 진영(브란드 1–3줄, 라임 7–9줄)의 빈칸에
        전사를 놓습니다. 외울 오프닝은 없고 얼음만 있으니, 호수를 읽고 나서 배치하세요. 모두 놓고
        나면 브란드가 먼저 움직입니다.
      </p>

      <h2>내 차례</h2>
      <p>
        전사 <strong>한 명</strong>을 움직입니다. 이동력을 써서 주변 8칸 중 하나로 한 걸음씩
        나아갑니다. 한 걸음의 비용은 <strong>1 + 두께 차이</strong>(오르막·내리막 모두)입니다. 평지
        1, 한 단계 2, 두 단계 3… 다른 전사를 통과하거나 구멍에 들어갈 수 없습니다. 움직일 수 있는
        전사가 없으면 차례를 넘깁니다.
      </p>

      <h2>밀치기</h2>
      <p>
        잡기는 없습니다. 이동의 마지막 한 걸음으로 적의 칸에 들어가면 그것이 <strong>밀치기</strong>
        입니다.
      </p>
      <ul>
        <li>
          <strong>내 힘</strong>은 내 전사의 힘에, 그 적에게 붙어 있는 내 다른 전사 1명당 +1입니다.
        </li>
        <li>
          <strong>방패벽</strong>은 적의 힘에, 그 적에게 붙어 있는 적의 동료 1명당 +1입니다.
        </li>
        <li>
          내 힘이 방패벽 <strong>이상</strong>이면 성공합니다. 실패할 밀치기는 둘 수 없습니다.
        </li>
        <li>
          적은 밀린 방향으로 한 칸 미끄러지고, <strong>그 뒤에 일렬로 선 전사들도</strong> 아군이든
          적이든 함께 미끄러집니다. 내 전사는 적이 비운 칸으로 들어갑니다.
        </li>
        <li>
          미끄러진 전사가 호수 밖이나 구멍에 닿거나, 원래 칸보다 2단계 이상 차이 나는 칸에 닿으면
          빠집니다. <strong>2단계 아래면 턱에서 떨어져</strong> 얼음을 깨고,{' '}
          <strong>2단계 위면 얼음벽에 부딪혀 짓눌립니다</strong>. 떨어지거나 짓눌리면 밀기는 거기서
          멈추고, 그 너머의 전사는 움직이지 않습니다.
        </li>
      </ul>

      <h2>해빙</h2>
      <p>
        3, 6, 9… 라운드가 끝날 때마다 얼음이 갈라지며 2칸에 금이 갑니다. 3라운드 뒤 금 간 칸은 한
        단계 얇아지고, 얇은 얼음(0)은 구멍이 되어 그 위의 전사가 빠집니다. 금은 미리 보이니 그 칸을
        피하거나, 적을 그 위로 밀어 넣으세요.
      </p>

      <h2>전사</h2>
      <p>각 진영에 종류별로 2명씩, 모두 6명입니다. 크기가 힘을 나타냅니다.</p>
      <Warriors text={text} />

      <h2>보드 읽기</h2>
      <p>
        내 전사를 고르면 어두운 점이 걸어갈 수 있는 칸을 보여 줍니다. 닿을 수 있는 적 위에는 내 힘 :
        방패벽 배지가 뜹니다. <Badge tone="works">3:2</Badge> 성공,{' '}
        <Badge tone="sinks-ally">3:2 !</Badge> 내 전사도 빠짐, <Badge tone="fails">1:3</Badge> 실패.
      </p>
      <p>
        적을 누르면 밀칠 수 있는 모든 경로가 나타납니다. 내 전사에서 시작하는 선, 밀치기가 시작되는
        칸의 마름모, 밀리는 방향의 화살표입니다. 반투명한 전사는 줄이 미끄러져 닿을 자리이고, 빠질
        전사에는 표시가 붙습니다. 원하는 마름모를 눌러 그 칸에서 밀칩니다.
      </p>
      <p>
        금 간 칸에는 균열이 보이고, 해빙이 가까울수록 더 세게 떨립니다. 구멍이 될 금은 빨간색입니다.
        룬스톤 부빙의 모든 칸에는 가장자리 안쪽에 가는 선이, 가운데 칸에는 룬스톤의 고리가 있습니다.
        둘 다 장악한 쪽의 색으로 빛나고, 아무도 장악하지 않으면 회색입니다.
      </p>
      <TryIt text={text} />
    </article>
  );
}

function Swatches({ text, label }: { text: LearnText; label: string }) {
  return (
    <ul className="page-swatches" aria-label={label}>
      {ICE_COLORS.map((color, h) => (
        <li key={color}>
          <span className="page-swatch" style={{ background: color }} aria-hidden="true" />
          {h} · {text.thickness[h]}
        </li>
      ))}
      <li>
        <span className="page-swatch" style={{ background: ICE_LOOK.water }} aria-hidden="true" />
        {text.hole}
      </li>
    </ul>
  );
}

function Warriors({ text }: { text: LearnText }) {
  const t = useT();
  return (
    <ul className="page-pieces">
      {FORCE.map((w) => (
        <li key={w.type}>
          <WarriorGlyph side="brand" type={w.type} size={44} />
          <div>
            <h3>
              {t.warriors[w.type]} <span className="label">×2</span>
            </h3>
            <p>{text.warriorStats(w.force, w.moves)}</p>
            <p>{text.warriorDetail[w.type]}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function TryIt({ text }: { text: LearnText }) {
  return (
    <p className="page-tryIt">
      <Link to="/play/bot" className="btn btn-primary btn-large">
        {text.tryIt}
      </Link>
    </p>
  );
}

function Badge({ tone, children }: { tone: keyof typeof TONE_COLORS; children: ReactNode }) {
  return (
    <span
      className="mono"
      style={{
        padding: '1px 7px',
        borderRadius: 999,
        background: TONE_COLORS[tone],
        color: '#0C1822',
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}
